import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Headphones, Play, Pause, Square, Stethoscope, User, Video, UserCheck, UserX, Copy, Mail, ExternalLink, Maximize, CircleDot, SlidersHorizontal, Music, FileText, Plus, ArrowLeft } from "lucide-react";
import { useDemoState } from "../demo/DemoStateProvider";
// Synced prod leaves (zero-coupling) resolved from src/vendor via the @/ alias.
import BallCanvas from "@/components/bilateral/BallCanvas";
import ControlPanel from "@/components/bilateral/ControlPanel";
import LivePreview from "@/components/bilateral/LivePreview";
import CheckinForm from "@/components/session/CheckinForm";
import PostSessionOverlay from "@/components/session/PostSessionOverlay";
import { DEFAULT_EMOTIONS } from "@/components/session/EmotionPicker";
import { SOUND_LIBRARY } from "@/components/soundboard/types";
import AudioEngine, { type AudioEngineHandle, type AudioPreset } from "@/components/audio/AudioEngine";
import { useGamepadHaptics } from "@/lib/haptics/useGamepadHaptics";
import { useBilateralBroadcast } from "@/hooks/use-bilateral-broadcast";
import type { CheckinActive, CheckinLiveData, PostCheckinData } from "@/lib/checkin-types";
import { cn } from "@/lib/utils";
import { SAMPLE_SCRIPT_LINES } from "@/lib/demo/sample-script";
import { TOPIC_CATEGORIES_SEED } from "@/lib/session-topics";
import { DEMO_SESSION_CODE, type CheckinSnapshot, type DemoSession, type SessionSettings } from "../demo/mock-data";
import { ClientPerspective } from "../demo/ClientPerspective";
import { DemoVideoTile } from "../demo/DemoVideoTile";
import { useSyncedState } from "../demo/live/useSyncedState";
import { HintTag } from "../demo/hints/HintTag";
import { useHints } from "../demo/hints/HintProvider";
import { useTour } from "../demo/tour/TourProvider";
import { track } from "../demo/analytics";

const BALL_COLORS = [
  { value: "#5BA9F7", label: "Sapphire" },
  { value: "#E5B970", label: "Gold" },
  { value: "#F2EBDD", label: "Cream" },
  { value: "#8FBC93", label: "Sage" },
];
const AUDIO_PRESETS: { value: Exclude<AudioPreset, "off">; label: string; sub: string }[] = [
  { value: "theta", label: "Theta", sub: "6 Hz · deep relaxation" },
  { value: "alpha", label: "Alpha", sub: "10 Hz · calm focus" },
  { value: "delta", label: "Delta", sub: "2 Hz · deep rest" },
];

/**
 * SessionView (Scope A2) — the live bilateral-stimulation session screen, composed
 * entirely from the real prod leaves (BallCanvas + AudioEngine + useGamepadHaptics).
 * Drives the ball locally with no socket and no backend. This is the "InteractiveDemo
 * pattern generalized" into the demo workspace.
 */
export function SessionView() {
  // A session can be started "for" a client (from their profile → /session?client=ID),
  // which attaches the client and applies their saved preferences. With no client the
  // session still runs but isn't attached (the guardrail teaches this — see Step 7).
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clientById, addSession } = useDemoState();
  const sessionClient = params.get("client") ? clientById(params.get("client") as string) : undefined;
  const prefs = sessionClient?.savedPrefs;
  const presetOrTheta = (p?: string): Exclude<AudioPreset, "off"> => (p === "alpha" || p === "delta" ? p : "theta");

  // The BLS console is SHARED in a co-driven demo — either person's change lands on
  // both screens (last click wins). Keyed per client so each client's saved
  // preferences still seed a fresh session. Plain useState outside a live room.
  const ck = (name: string) => `sv.console.${sessionClient?.id ?? "none"}.${name}`;
  const [isRunning, setIsRunning] = useSyncedState(ck("isRunning"), false);
  const [hz, setHz] = useSyncedState(ck("hz"), prefs?.hz ?? 0.7);
  const [ballSize, setBallSize] = useSyncedState(ck("ballSize"), 30);
  const [ballColor, setBallColor] = useSyncedState(ck("ballColor"), prefs?.ballColor ?? "#4a6fa5");
  const [canvasBg, setCanvasBg] = useSyncedState(ck("canvasBg"), prefs?.canvasBg ?? "#0D1B2E");
  const [audioOn, setAudioOn] = useSyncedState(ck("audioOn"), prefs?.audioOn ?? false);
  const [audioPreset, setAudioPreset] = useSyncedState<string>(ck("audioPreset"), presetOrTheta(prefs?.audioPreset));
  const [audioVolume, setAudioVolume] = useSyncedState(ck("audioVolume"), prefs?.audioVolume ?? 0.3);
  const [audioPresetIds, setAudioPresetIds] = useState<string[]>(["theta", "alpha", "delta"]);
  const [hapticsOn, setHapticsOn] = useSyncedState(ck("hapticsOn"), prefs?.hapticsOn ?? false);
  const [hapticsIntensity, setHapticsIntensity] = useSyncedState(ck("hapticsIntensity"), prefs?.hapticsIntensity ?? 0.8);
  // ControlPanel extras (passes limit, countdown timer, interface theme)
  const [passLimit, setPassLimit] = useState(20);
  const [passLimitOn, setPassLimitOn] = useState(false);
  const [timerOn, setTimerOn] = useState(false);
  const [timerDuration, setTimerDuration] = useState(300);
  const [interfaceTheme, setInterfaceTheme] = useState("posh-clinical");

  const audioRef = useRef<AudioEngineHandle>(null);
  const { pulse, status: hapticsStatus, controllerLabel } = useGamepadHaptics({
    enabled: hapticsOn && isRunning,
    intensity: hapticsIntensity,
  });

  // Co-browsed so the guest follows the presenter (therapist⇄client view, active tab,
  // and the typed session focus). useSyncedState === useState in the static demo.
  const [perspective, setPerspective] = useSyncedState<"therapist" | "client">("sv.perspective", "therapist");
  const [videoOpen, setVideoOpen] = useState(false);
  // Session console chrome: SESSION / SOUNDS / SCRIPTS sub-tabs + a collapsible
  // ControlPanel (the "Controls" toolbar toggle, like the real session).
  const [consoleTab, setConsoleTab] = useSyncedState<"session" | "sounds" | "scripts">("sv.consoleTab", "session");
  const [controlsOpen, setControlsOpen] = useState(true);
  // The floating ControlPanel popup shown over the Sounds/Scripts tabs (default closed).
  const [floatingControlsOpen, setFloatingControlsOpen] = useState(false);
  const [firstStartAt, setFirstStartAt] = useState<number | null>(null);

  // Session focus (Theme → Topic → Target emotion) — what makes saved history meaningful.
  const [theme, setTheme] = useSyncedState("sv.theme", "");
  const [topic, setTopic] = useSyncedState("sv.topic", "");
  const [targetEmotion, setTargetEmotion] = useSyncedState("sv.targetEmotion", "");

  // ── Pre/Post check-in (controlled, broadcast over the loopback so the Client
  //    view mirrors the exact form the therapist pushed — same wiring as prod). ──
  // Co-browsed (sv.* keys): checkin_state/checkin_data only reach the CLIENT-VIEW
  // mirror — two SessionViews both in therapist perspective never hear each other,
  // so without these keys the guest saw nothing when the host opened a check-in.
  const [showPreCheckin, setShowPreCheckin] = useSyncedState("sv.showPreCheckin", false);
  const [showPostSession, setShowPostSession] = useSyncedState("sv.showPostSession", false);
  const [preGoal, setPreGoal] = useSyncedState("sv.pre.goal", "");
  const [preEmotions, setPreEmotions] = useSyncedState<string[]>("sv.pre.emotions", []);
  const [preDistress, setPreDistress] = useSyncedState<number | null>("sv.pre.distress", null);
  const [preCapability, setPreCapability] = useSyncedState<number | null>("sv.pre.capability", null);
  const [preNote, setPreNote] = useSyncedState("sv.pre.note", "");
  const [postEmotions, setPostEmotions] = useSyncedState<string[]>("sv.post.emotions", []);
  const [postDistress, setPostDistress] = useSyncedState<number | null>("sv.post.distress", null);
  const [postCapability, setPostCapability] = useSyncedState<number | null>("sv.post.capability", null);
  const [postNote, setPostNote] = useSyncedState("sv.post.note", "");
  const [saving, setSaving] = useState(false);
  // Live telemetry shown in the session header.
  const [elapsedSec, setElapsedSec] = useState(0);
  const [passCount, setPassCount] = useState(0);
  // Session timer — ticks each second while the ball runs.
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);
  // ControlPanel transport handlers + countdown/elapsed display.
  const onStart = () => { setIsRunning(true); setFirstStartAt((f) => f ?? Date.now()); };
  const onStop = () => setIsRunning(false);
  const onReset = () => { setIsRunning(false); setElapsedSec(0); setPassCount(0); setFirstStartAt(null); };
  // Count a completed pass; when a passes-limit is armed, auto-pause AT the limit so
  // the therapist can reset for the next "set" (mirrors the real session — page.tsx
  // handlePassComplete). The limit is checked only on a NEW pass (inside the functional
  // updater), so Reset → Start cleanly runs the next set. Deferred setState avoids
  // nesting setIsRunning inside the setPassCount updater.
  const handlePassComplete = useCallback((side: "left" | "right") => {
    pulse(side);
    setPassCount((prev) => {
      const next = prev + 1;
      if (passLimitOn && next >= passLimit) setTimeout(() => setIsRunning(false), 0);
      return next;
    });
  }, [pulse, passLimitOn, passLimit]);
  // Whether the floating LivePreview (minimized draggable canvas) should show — only
  // on the Sounds/Scripts sub-tabs, where the big canvas isn't on screen. (Collapsing
  // the controls now just widens the canvas, which stays visible, so no preview needed.)
  const showLivePreview = perspective === "therapist" && consoleTab !== "session";
  const timerRemaining = Math.max(0, timerDuration - elapsedSec);
  const timerDisplay = timerOn ? fmtClock(timerRemaining) : fmtClock(elapsedSec);
  const timerExpiring = timerOn && isRunning && timerRemaining <= 30;
  const { autoShow } = useHints();
  const { active: tourActive } = useTour();
  // Flip perspective; the first time the prospect lands in Client view, auto-expand
  // the explanatory hint — unless a guided tour is driving (it explains this itself).
  function changePerspective(p: "therapist" | "client") {
    setPerspective(p);
    track("perspective_toggled", { to: p });
    if (p === "client" && !tourActive) autoShow("hint-client-view");
  }

  // Fire "reached_bls" once, the first time the ball runs.
  const reachedRef = useRef(false);
  useEffect(() => {
    if (isRunning && !reachedRef.current) {
      reachedRef.current = true;
      track("reached_bls");
    }
  }, [isRunning]);

  // Bridge therapist → client mirror over the loopback socket (same tab, no network).
  const {
    broadcastSync,
    broadcastSessionEnd,
    broadcastSessionSaved,
    broadcastCheckinState,
    broadcastCheckinData,
    clientCheckinPhaseComplete,
    clientCheckinData,
  } = useBilateralBroadcast(DEMO_SESSION_CODE);
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    if (isRunning && startRef.current == null) startRef.current = performance.now();
    else if (!isRunning) startRef.current = null;
  }, [isRunning]);
  // Feed THIS screen's Client-view mirror (same tab). In a co-driven room the `frame`
  // event stays on this device (socket shim LOCAL_ONLY): both consoles are already
  // identical through the synced keys above, so each screen's mirror follows its own
  // console — relaying both screens' frames would make each mirror flicker.
  // Always bound to the latest settings (avoids stale closures in the heartbeat).
  const pushStateRef = useRef<() => void>(() => {});
  pushStateRef.current = () => {
    const elapsed = isRunning && startRef.current != null ? (performance.now() - startRef.current) / 1000 : 0;
    broadcastSync(0.5, hz, elapsed, ballSize, ballColor, canvasBg, isRunning, audioOn, audioVolume, audioPreset, hapticsOn, hapticsIntensity);
  };
  // Push on any setting change + when flipping to Client view (so a freshly-mounted
  // MirrorCanvas reflects current state at once)…
  useEffect(() => { pushStateRef.current(); }, [hz, ballSize, ballColor, canvasBg, isRunning, audioOn, audioVolume, audioPreset, hapticsOn, hapticsIntensity, perspective]);
  // …plus a 2s heartbeat so the mirror's "connected" status (3s timeout) stays warm.
  useEffect(() => {
    const id = setInterval(() => pushStateRef.current(), 2000);
    return () => clearInterval(id);
  }, []);

  // ── Live-synced check-in answers (mirrors the real session page) ──────────
  // The forms are fully controlled so the same answers mirror to/from the Client
  // view. Local edits broadcast (debounced); inbound client edits are applied
  // below WITHOUT re-broadcasting (avoids the ping-pong loop).
  const preLive: CheckinLiveData = { phase: "pre", goal: preGoal, emotions: preEmotions, distress: preDistress, capability: preCapability, note: preNote };
  const postLive: CheckinLiveData = { phase: "post", goal: "", emotions: postEmotions, distress: postDistress, capability: postCapability, note: postNote };

  const checkinBroadcastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleCheckinBroadcast = useCallback((data: CheckinLiveData) => {
    if (checkinBroadcastTimer.current) clearTimeout(checkinBroadcastTimer.current);
    checkinBroadcastTimer.current = setTimeout(() => broadcastCheckinData(data), 120);
  }, [broadcastCheckinData]);
  useEffect(() => () => { if (checkinBroadcastTimer.current) clearTimeout(checkinBroadcastTimer.current); }, []);

  const handlePreChange = useCallback((next: CheckinLiveData) => {
    setPreGoal(next.goal); setPreEmotions(next.emotions); setPreDistress(next.distress);
    setPreCapability(next.capability); setPreNote(next.note);
    scheduleCheckinBroadcast(next);
  }, [scheduleCheckinBroadcast]);
  const handlePostChange = useCallback((next: CheckinLiveData) => {
    setPostEmotions(next.emotions); setPostDistress(next.distress);
    setPostCapability(next.capability); setPostNote(next.note);
    scheduleCheckinBroadcast(next);
  }, [scheduleCheckinBroadcast]);

  // Apply inbound client edits to whichever form is open (no re-broadcast).
  useEffect(() => {
    const d = clientCheckinData;
    if (!d) return;
    if (d.phase === "pre" && showPreCheckin) {
      setPreGoal(d.goal); setPreEmotions(d.emotions); setPreDistress(d.distress);
      setPreCapability(d.capability); setPreNote(d.note);
    } else if (d.phase === "post" && showPostSession) {
      setPostEmotions(d.emotions); setPostDistress(d.distress);
      setPostCapability(d.capability); setPostNote(d.note);
    }
  }, [clientCheckinData, showPreCheckin, showPostSession]);

  // Push the pre check-in to the client AND open our copy — the ONLY way a
  // check-in opens (no automatic/timer trigger), exactly like the real app.
  const openPreCheckin = useCallback(() => {
    setShowPreCheckin(true);
    broadcastCheckinState("pre");
    broadcastCheckinData({ phase: "pre", goal: preGoal, emotions: preEmotions, distress: preDistress, capability: preCapability, note: preNote });
  }, [broadcastCheckinState, broadcastCheckinData, preGoal, preEmotions, preDistress, preCapability, preNote]);
  const handlePreDone = useCallback(() => {
    setShowPreCheckin(false);
    broadcastCheckinState(null);
  }, [broadcastCheckinState]);
  const openPostCheckin = useCallback(() => {
    setShowPostSession(true);
    broadcastCheckinState("post");
    broadcastCheckinData({ phase: "post", goal: "", emotions: postEmotions, distress: postDistress, capability: postCapability, note: postNote });
  }, [broadcastCheckinState, broadcastCheckinData, postEmotions, postDistress, postCapability, postNote]);
  const handleClosePostCheckin = useCallback(() => {
    setShowPostSession(false);
    broadcastCheckinState(null);
  }, [broadcastCheckinState]);

  // React to the CLIENT submitting a check-in (over the loopback). Submitting the
  // pre check-in dismisses it on both sides; the post stays until the therapist saves.
  const checkinCompleteRef = useRef<(phase: "pre" | "post") => void>(() => {});
  checkinCompleteRef.current = (phase) => {
    if (phase === "pre") { setShowPreCheckin(false); broadcastCheckinState(null); }
  };
  useEffect(() => {
    if (!clientCheckinPhaseComplete) return;
    checkinCompleteRef.current(clientCheckinPhaseComplete);
  }, [clientCheckinPhaseComplete]);

  // The loopback socket has NO server (so no sync_request replay). When flipping to
  // Client view, the freshly-mounted mirror has missed any earlier checkin_state —
  // re-push the current form + answers so it shows. (Child effects run before this
  // parent effect, so the mirror's listener is already registered.) checkin_data is
  // re-pushed only here, never on the heartbeat, so the client's typing is never clobbered.
  useEffect(() => {
    if (perspective !== "client") return;
    const active: CheckinActive = showPreCheckin ? "pre" : showPostSession ? "post" : null;
    broadcastCheckinState(active);
    if (active === "pre") {
      broadcastCheckinData(preLive);
    } else if (active === "post") {
      // The mirror wasn't mounted during the pre check-in (and the loopback has no
      // sync_request replay), so the post form's pre→post comparison would be empty.
      // Re-send the pre answers FIRST (only if any were recorded) so the mirror's
      // pre-capture effect repopulates preAnswers, THEN the post payload so
      // activeCheckin/liveData settle on "post" (the pre payload can't clobber the
      // post form — liveData is gated on checkinData.phase === activeCheckin).
      const hasPre = !!(preGoal || preEmotions.length || preDistress !== null || preCapability !== null || preNote);
      if (hasPre) broadcastCheckinData(preLive);
      broadcastCheckinData(postLive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perspective]);

  // End session → stop the ball, signal the client, open the post check-in (save path).
  const handleEndSession = useCallback(() => {
    setIsRunning(false);
    broadcastSessionEnd();
    openPostCheckin();
  }, [broadcastSessionEnd, openPostCheckin]);

  // "Controls" reveals the control panel WITHOUT leaving your current tab. On the
  // Session tab it collapses/expands the inline left column; on Sounds/Scripts it
  // pops the panel up as a floating overlay (so you can tweak the ball while your
  // script stays on screen — NOT yank you back to the canvas). Two states so the
  // floating popup never auto-opens just because the inline sidebar was left open.
  const handleToggleControls = useCallback(() => {
    if (consoleTab === "session") setControlsOpen((o) => !o);
    else setFloatingControlsOpen((o) => !o);
  }, [consoleTab]);
  // Returning to the Session tab hands control back to the inline sidebar.
  useEffect(() => { if (consoleTab === "session") setFloatingControlsOpen(false); }, [consoleTab]);

  // Save & End: build a DemoSession from this run and append it to the client's
  // history — the demo's payoff (start a session for a client → it appears under
  // their profile). Guardrail: a session with no client attached can't be saved.
  const handlePostSessionSave = useCallback((notes: string, postCheckin: PostCheckinData) => {
    if (!sessionClient) {
      // No client attached → nothing to save to history, but the therapist can still
      // END the session. (The "No client attached" banner already explains this; the
      // demo never blocks you from ending — Step 7 adds the proper start-time guardrail.)
      broadcastSessionSaved();
      broadcastCheckinState(null);
      setShowPostSession(false);
      navigate("/dashboard");
      return;
    }
    setSaving(true);
    const settings: SessionSettings = { ballColor, canvasBg, hz, audioOn, audioPreset, audioVolume, hapticsOn, hapticsIntensity, videoOn: videoOpen };
    // Store raw scale values (null = skipped) — never a fabricated 0 reading. The
    // saved `note` is the CLIENT-SHARED note only; the therapist-private `notes`
    // arg is intentionally NOT persisted (the snapshot has no private-note field).
    const pre: CheckinSnapshot = { distress: preDistress, capability: preCapability, emotions: preEmotions, goal: preGoal || undefined, note: preNote || undefined };
    const post: CheckinSnapshot = { distress: postCheckin.distress, capability: postCheckin.capability, emotions: postCheckin.emotions, note: postCheckin.note || undefined };
    const session: DemoSession = {
      id: `s-${sessionClient.id}-${Date.now()}`,
      clientId: sessionClient.id,
      date: new Date().toISOString().slice(0, 10),
      durationMin: Math.max(1, Math.round(elapsedSec / 60)),
      modality: sessionClient.modality,
      theme: theme || "Other",
      topic: topic || "Untitled session",
      targetEmotion: targetEmotion || "—",
      settings, pre, post,
    };
    addSession(session);
    broadcastSessionSaved();
    broadcastCheckinState(null);
    setSaving(false);
    setShowPostSession(false);
    navigate(`/client/${sessionClient.id}`);
  }, [sessionClient, ballColor, canvasBg, hz, audioOn, audioPreset, audioVolume, hapticsOn, hapticsIntensity, videoOpen, preDistress, preCapability, preEmotions, preGoal, preNote, elapsedSec, theme, topic, targetEmotion, addSession, broadcastSessionSaved, broadcastCheckinState, navigate]);

  let controllerLine = "Connect a PS5 DualSense or Xbox controller, then press any button.";
  if (hapticsStatus === "unsupported") controllerLine = "Haptics need Chrome, Edge, or another Chromium browser.";
  else if (hapticsStatus === "ready") controllerLine = `🎮 ${controllerLabel ?? "Controller"} paired — feel the rumble.`;

  // The REAL ControlPanel (reused verbatim from prod; driven entirely by demo state),
  // extracted so it renders either inline on the Session tab OR in the floating popup
  // over Sounds/Scripts. Those two surfaces are mutually exclusive by tab, so the
  // element is only ever mounted once. The `demo-cp` marker drives app.css padding
  // fixes for the vendored panel (it can't be hand-edited — sync overwrites src/vendor).
  const controlPanelEl = (
    <div className="demo-cp">
      <ControlPanel
        hz={hz} setHz={setHz}
        ballSize={ballSize} setBallSize={setBallSize}
        ballColor={ballColor} setBallColor={setBallColor}
        canvasBg={canvasBg} setCanvasBg={setCanvasBg}
        isRunning={isRunning} isStopping={false}
        onStart={onStart} onStop={onStop} onReset={onReset}
        passCount={passCount} passLimit={passLimit} setPassLimit={setPassLimit}
        passLimitOn={passLimitOn} setPassLimitOn={setPassLimitOn}
        timerOn={timerOn} setTimerOn={setTimerOn}
        timerDuration={timerDuration} setTimerDuration={setTimerDuration}
        timerDisplay={timerDisplay} timerExpiring={timerExpiring}
        interfaceTheme={interfaceTheme} setInterfaceTheme={setInterfaceTheme}
        audioOn={audioOn} setAudioOn={setAudioOn}
        audioVolume={audioVolume} setAudioVolume={setAudioVolume}
        audioPreset={audioPreset} setAudioPreset={setAudioPreset}
        audioPresetIds={audioPresetIds} onAudioPresetIdsChange={setAudioPresetIds}
        userAudioFiles={[]}
        hapticsOn={hapticsOn} setHapticsOn={setHapticsOn}
        hapticsIntensity={hapticsIntensity} setHapticsIntensity={setHapticsIntensity}
        hapticsStatus={hapticsStatus} hapticsControllerLabel={controllerLabel ?? null}
      />
    </div>
  );

  return (
    <main className="session-page mx-auto max-w-7xl px-4 py-6">
      {/* Exit hatch — the global nav is hidden on /session, so this is how you leave
          the session and return to the demo workspace. */}
      <div className="mb-3">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Exit session
        </button>
      </div>
      {perspective === "therapist" &&
        (sessionClient ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] px-4 py-2">
            <UserCheck className="h-4 w-4 text-[var(--color-gold-soft)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-gold-soft)]">Session for {sessionClient.displayName}</span>
            <span className="text-[12px] text-[var(--color-text-ghost)]">· saved preferences applied · saves to history</span>
          </div>
        ) : (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-4 py-2">
            <UserX className="h-4 w-4 text-[var(--color-text-ghost)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">No client attached</span>
            <span className="text-[12px] text-[var(--color-text-ghost)]">· this session won&apos;t be saved to history</span>
          </div>
        ))}
      <PerspectiveToggle perspective={perspective} onChange={changePerspective} />

      {perspective === "client" ? (
        <div className="mt-4">
          <ClientPerspective sessionCode={DEMO_SESSION_CODE} />
        </div>
      ) : (
      <div className="mt-4">
        <ConsoleToolbar
          tab={consoleTab}
          setTab={setConsoleTab}
          controlsOpen={consoleTab === "session" ? controlsOpen : floatingControlsOpen}
          onToggleControls={handleToggleControls}
          onStartVideo={() => setVideoOpen(true)}
          onPreCheckin={openPreCheckin}
          onPostCheckin={openPostCheckin}
          preActive={showPreCheckin}
          postActive={showPostSession}
          onEnd={handleEndSession}
        />
        {consoleTab === "scripts" && <ScriptsView />}
        {consoleTab === "sounds" && <SoundsView onAddToSession={() => setConsoleTab("session")} />}
        {consoleTab === "session" && (
        // Canvas-dominant layout mirroring the real session: a fixed scrollable
        // controls column on the left + a big canvas filling the rest on the right.
        // `order` puts controls first (left) without moving the large JSX blocks.
        <div className={cn(
          // Fill the viewport below the demo chrome so the canvas dominates and the
          // controls column scrolls internally (like the real session) — not the page.
          "mt-4 grid gap-4 lg:h-[calc(100vh-280px)] lg:min-h-[440px] lg:overflow-hidden",
          controlsOpen ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1",
        )}>
      {/* ── Canvas column (dominant, right) ── */}
      <section className="flex flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-void)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] lg:order-2 lg:h-full">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-navy-mid)] px-5 py-2.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">
            ● Live session{theme ? ` · ${theme}` : ""}{topic ? ` · ${topic}` : ""}
          </span>
          <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
            <span className="tabular-nums text-[var(--color-text-primary)]">{fmtClock(elapsedSec)}</span>
            <span aria-hidden>·</span>
            <span className="tabular-nums">{passCount} passes</span>
            <span aria-hidden>·</span>
            <span>{isRunning ? "Running" : "Paused"}</span>
          </span>
        </div>

        <div className="relative h-[340px] w-full sm:h-[420px] lg:h-auto lg:flex-1">
          <BallCanvas
            hz={hz}
            ballSize={ballSize}
            ballColor={ballColor}
            canvasBg={canvasBg}
            isRunning={isRunning}
            isStopping={false}
            onPositionChange={(xNorm) => audioRef.current?.updateXNorm(xNorm)}
            onPassComplete={handlePassComplete}
            fillContainer
          />
          {!isRunning && (
            <button
              type="button"
              onClick={() => setIsRunning(true)}
              aria-label="Start the bilateral stimulation"
              className="group absolute inset-0 z-20 flex flex-col items-center justify-center gap-2.5 bg-[rgba(13,18,23,0.66)] backdrop-blur-[2px] transition-colors hover:bg-[rgba(13,18,23,0.52)]"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-border-gold)] bg-[var(--color-navy-mid)] text-[var(--color-gold-soft)] transition-transform group-hover:scale-105">
                <Play className="h-7 w-7 translate-x-[1px]" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">
                Tap to start the ball
              </span>
            </button>
          )}
          <AudioEngine ref={audioRef} isRunning={isRunning} audioOn={audioOn} volume={audioVolume} preset={audioPreset as AudioPreset} />
        </div>

        {/* Transport */}
        <div className="flex items-center gap-3 border-t border-[var(--color-border)] bg-[var(--color-navy-mid)] px-5 py-3">
          <button
            type="button"
            onClick={() => setIsRunning((r) => !r)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-navy-light)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)]"
          >
            {isRunning ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {isRunning ? "Pause" : "Resume"}
          </button>
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-navy-light)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
          >
            <Video className="h-3.5 w-3.5" /> Start video
          </button>
          <button
            type="button"
            onClick={handleEndSession}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            <Square className="h-3.5 w-3.5" /> End &amp; review check-in
          </button>
        </div>
      </section>

      {/* ── Control sidebar (left, scrollable) — collapsible via the Controls toolbar button ── */}
      {controlsOpen && (
      <aside className="flex flex-col gap-4 lg:order-1 lg:h-full lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1">
        {/* Session focus — Theme → Topic → Target emotion (drives saved history) */}
        <div className="grid gap-2.5 rounded-lg border border-[var(--color-border-gold)] bg-[var(--color-navy-mid)] px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-gold-soft)]">Session focus</p>
          <FieldLabel label="Theme">
            <select value={theme} onChange={(e) => setTheme(e.target.value)} className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-2.5 py-2 font-mono text-[12px] text-[var(--color-text-primary)]">
              <option value="">Select a theme…</option>
              {TOPIC_CATEGORIES_SEED.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Specific topic">
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Recent fight with my brother" maxLength={80}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-2.5 py-2 font-mono text-[12px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]" />
          </FieldLabel>
          <FieldLabel label="Target emotion">
            <input value={targetEmotion} onChange={(e) => setTargetEmotion(e.target.value)} placeholder="e.g. Anger" maxLength={40}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-2.5 py-2 font-mono text-[12px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]" />
          </FieldLabel>
        </div>

        {/* The REAL ControlPanel (extracted to controlPanelEl so it reuses identically
            in the floating popup over Sounds/Scripts). */}
        {controlPanelEl}

        {/* Sample script (kept) */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">Sample script · not clinical guidance</p>
          <ol className="mt-2 max-h-[120px] space-y-2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5">
            {SAMPLE_SCRIPT_LINES.slice(0, 6).map((line, i) => (
              <li key={i} className="flex gap-2 text-[12px] leading-relaxed text-[var(--color-text-primary)]">
                <span className="mt-0.5 shrink-0 font-mono text-[9px] text-[var(--color-text-ghost)]">{String(i + 1).padStart(2, "0")}</span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
        </div>
      </aside>
      )}
        </div>
        )}
      </div>
      )}

      {showLivePreview && (
        <LivePreview
          hz={hz} ballSize={ballSize} ballColor={ballColor} canvasBg={canvasBg}
          isRunning={isRunning} isStopping={false}
          onPositionChange={(x) => audioRef.current?.updateXNorm(x)}
          onPassComplete={handlePassComplete}
          onStopComplete={() => {}}
          onStart={onStart} onStop={onStop}
          timerOn={timerOn} setTimerOn={setTimerOn}
          timerDuration={timerDuration} setTimerDuration={setTimerDuration}
          timerDisplay={timerDisplay} timerExpiring={timerExpiring}
          firstStartAt={firstStartAt}
        />
      )}
      {videoOpen && <DemoVideoTile onClose={() => setVideoOpen(false)} />}

      {/* Floating Controls popup over the Sounds/Scripts tabs — lets the therapist
          tweak the ball WITHOUT leaving their script (the old behaviour yanked them
          back to the canvas). Portaled to <body> so the z-1 <main> context can't trap
          it; z-[65] sits above the z-60 banner but below the z-[70] check-in modals. */}
      {floatingControlsOpen && consoleTab !== "session" && createPortal(
        <div className="fixed right-3 top-[64px] z-[65] flex max-h-[82vh] w-[360px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-navy-mid)] px-4 py-2.5">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-gold-soft)]">
              <SlidersHorizontal className="h-3 w-3" /> Controls
            </span>
            <button
              type="button"
              onClick={() => setFloatingControlsOpen(false)}
              aria-label="Close controls"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-1 font-mono text-[9px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
            >
              Close ×
            </button>
          </div>
          <div className="overflow-y-auto p-4">
            {controlPanelEl}
          </div>
        </div>,
        document.body,
      )}

      {/* Therapist-side check-in overlays. Gated to therapist view so they never
          stack over the Client mirror's own copy (the two render the same form,
          kept in sync over the loopback — like two devices). PORTALED to <body> in
          a z-[70] wrapper: the page <main> is a z-1 stacking context (app.css:186
          lifts content above the background gradient), which would trap the modal
          BELOW the z-60 demo banner. Portaling escapes that context so z-[70] beats
          the banner; it also immunises the modal against any overflow/transform trap. */}
      {perspective === "therapist" && showPreCheckin && createPortal(
        <div className="relative z-[70]">
          <CheckinForm
            value={preLive}
            onChange={handlePreChange}
            emotions={DEFAULT_EMOTIONS}
            showGoal
            title="Pre-Session Check-in"
            subtitle="Before we begin"
            liveHint="Shared live with the client"
            primaryLabel="Done"
            onPrimary={handlePreDone}
            secondaryLabel="Cancel"
            onSecondary={handlePreDone}
          />
        </div>,
        document.body,
      )}
      {perspective === "therapist" && showPostSession && createPortal(
        <div className="relative z-[70]">
          <PostSessionOverlay
            durationSeconds={elapsedSec}
            passCount={passCount}
            clientName={sessionClient?.displayName ?? null}
            audioPreset={audioOn ? audioPreset : null}
            onSave={handlePostSessionSave}
            onClose={handleClosePostCheckin}
            saving={saving}
            preGoal={preGoal || null}
            preEmotions={preEmotions}
            preDistress={preDistress}
            preCapability={preCapability}
            preNote={preNote || null}
            emotions={DEFAULT_EMOTIONS}
            value={postLive}
            onChange={handlePostChange}
          />
        </div>,
        document.body,
      )}
    </main>
  );
}

// ── Local control primitives (demo-authored UI; not synced) ───────────────────
function Slider({ label, value, min, max, step, unit, fmt, onChange, hintId, hintTitle, hintBody }: { label: string; value: number; min: number; max: number; step: number; unit?: string; fmt: (v: number) => string; onChange: (v: number) => void; hintId?: string; hintTitle?: string; hintBody?: React.ReactNode }) {
  const { autoShow } = useHints();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-muted)]">
        <span className="flex items-center gap-2">
          <span>{label}</span>
          {hintId && <HintTag id={hintId} title={hintTitle}>{hintBody}</HintTag>}
        </span>
        <span className="text-[var(--color-gold-soft)]">{fmt(value)}{unit ?? ""}</span>
      </span>
      <input aria-label={label} type="range" min={min} max={max} step={step} value={value} onChange={(e) => { onChange(Number(e.target.value)); if (hintId) autoShow(hintId); }} className="h-1 w-full cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-gold-soft)]" />
    </div>
  );
}

function Toggle({ checked, onChange, label, icon, hintId, hintTitle, hintBody }: { checked: boolean; onChange: (v: boolean) => void; label: string; icon: React.ReactNode; hintId?: string; hintTitle?: string; hintBody?: React.ReactNode }) {
  const { autoShow } = useHints();
  return (
    <div className={cn("flex w-full items-center gap-3 rounded-lg border px-4 py-3 transition-colors", checked ? "border-[var(--color-border-gold)] bg-[var(--color-navy-light)]" : "border-[var(--color-border)] bg-[var(--color-bg-deep)]")}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => { onChange(!checked); if (hintId) autoShow(hintId); }}
        className="flex flex-1 items-center gap-3 text-left"
      >
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md border", checked ? "border-[var(--color-border-gold)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]")} aria-hidden>{icon}</span>
        <span className="font-heading text-[15px] font-medium text-[var(--color-text-primary)]">{label}</span>
        <span aria-hidden className={cn("relative ml-auto h-6 w-11 shrink-0 rounded-full border transition-colors", checked ? "border-[var(--color-gold)] bg-[var(--color-gold-soft)]" : "border-[var(--color-border)] bg-[var(--color-bg-deep)]")}>
          <span className={cn("absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-[var(--color-text-primary)] transition-all", checked ? "left-[22px]" : "left-1")} />
        </span>
      </button>
      {hintId && <HintTag id={hintId} title={hintTitle} side="top">{hintBody}</HintTag>}
    </div>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{label}</span>
      {children}
    </label>
  );
}

function fmtClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** The real session console toolbar — session id + SESSION/SOUNDS/SCRIPTS sub-tabs
 *  and the action row. Buttons wrap (flex-wrap) so they never collide. */
function ConsoleToolbar({
  tab, setTab, controlsOpen, onToggleControls, onStartVideo, onPreCheckin, onPostCheckin, preActive, postActive, onEnd,
}: {
  tab: "session" | "sounds" | "scripts";
  setTab: (t: "session" | "sounds" | "scripts") => void;
  controlsOpen: boolean;
  onToggleControls: () => void;
  onStartVideo: () => void;
  onPreCheckin: () => void;
  onPostCheckin: () => void;
  preActive: boolean;
  postActive: boolean;
  onEnd: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const subTabs = [
    { k: "session" as const, label: "Session", icon: <Play className="h-3 w-3" /> },
    { k: "sounds" as const, label: "Sounds", icon: <Music className="h-3 w-3" /> },
    { k: "scripts" as const, label: "Scripts", icon: <FileText className="h-3 w-3" /> },
  ];
  const copyLink = () => {
    try { navigator.clipboard?.writeText("https://demo.psy180.com/view/PSK-DEMO"); } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">Session PSK-DEMO · <span className="text-[var(--color-text-muted)]">Paused</span></span>
        <div className="inline-flex rounded-lg border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-1">
          {subTabs.map((s) => (
            <button key={s.k} type="button" onClick={() => setTab(s.k)}
              className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide transition-colors",
                tab === s.k ? "bg-[var(--color-gold)] text-[var(--color-bg-deep)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-gold-soft)]")}>
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <TBtn icon={<Copy className="h-3 w-3" />} label={copied ? "Copied!" : "Copy client link"} onClick={copyLink} />
        <TBtn icon={<Mail className="h-3 w-3" />} label="Email link" />
        <TBtn icon={<ExternalLink className="h-3 w-3" />} label="Open" />
        <TBtn icon={<Maximize className="h-3 w-3" />} label="Full screen" onClick={() => document.documentElement.requestFullscreen?.().catch(() => {})} />
        <TBtn icon={<CircleDot className="h-3 w-3" />} label="Pre check-in" gold active={preActive} onClick={onPreCheckin} />
        <TBtn icon={<CircleDot className="h-3 w-3" />} label="Post check-in" gold active={postActive} onClick={onPostCheckin} />
        <TBtn icon={<Video className="h-3 w-3" />} label="Start video" gold onClick={onStartVideo} />
        <TBtn icon={<SlidersHorizontal className="h-3 w-3" />} label="Controls" active={controlsOpen} onClick={onToggleControls} />
        <button type="button" onClick={onEnd} className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[var(--color-danger)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger)]/15">
          End session
        </button>
      </div>
    </div>
  );
}

function TBtn({ icon, label, onClick, gold, active }: { icon: React.ReactNode; label: string; onClick?: () => void; gold?: boolean; active?: boolean }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide transition-colors",
        active ? "border-[var(--color-gold)] bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]"
        : gold ? "border-[var(--color-border-gold)] text-[var(--color-gold-soft)] hover:bg-[var(--color-navy-light)]"
        : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]")}>
      {icon} {label}
    </button>
  );
}

/** SCRIPTS sub-view — shows how a therapist keeps their own scripts on hand during
 *  a session. The documents are ORIGINAL sample wording written for the demo — not
 *  any real, branded, or proprietary protocol. Therapists bring their own. */
function ScriptsView() {
  // Co-browsed: the guest's session Scripts tab follows the driver's selection.
  const [active, setActive] = useSyncedState<"grounding" | "processing">("sv.scriptId", "grounding");
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[260px_1fr]">
      <aside className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-3">
        <p className="px-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">Scripts</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["All", "Grounding", "Processing"].map((c) => (
            <span key={c} className="rounded-md border border-[var(--color-border)] px-2 py-1 font-mono text-[9px] uppercase tracking-wide text-[var(--color-text-ghost)]">{c}</span>
          ))}
        </div>
        <p className="mt-4 px-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">Grounding</p>
        <ScriptItem name="Grounding &amp; Resourcing (sample)" sel={active === "grounding"} onClick={() => setActive("grounding")} />
        <p className="mt-3 px-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">Processing</p>
        <ScriptItem name="Target Processing Sequence (sample)" sel={active === "processing"} onClick={() => setActive("processing")} />
      </aside>
      <div className="max-h-[68vh] overflow-y-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-4">
        <FakeScriptPdf script={active} />
      </div>
    </div>
  );
}

function ScriptItem({ name, sel, onClick }: { name: string; sel: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("mt-1 flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors",
        sel ? "bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]" : "text-[var(--color-text-muted)] hover:bg-[var(--color-navy-mid)]")}>
      <span className="truncate text-[12px]">{name}</span>
      <span className="shrink-0 font-mono text-[9px] text-[var(--color-text-ghost)]">PDF</span>
    </button>
  );
}

/** A styled "paper" document showing how an uploaded script looks in-session.
 *  The wording is ORIGINAL, written for this demo — it is not any real, branded,
 *  or proprietary clinical protocol. Therapists upload their own scripts. */
function FakeScriptPdf({ script }: { script: "grounding" | "processing" }) {
  return (
    <div className="mx-auto max-w-[760px] rounded-md bg-white px-10 py-9 text-[#1a1a1a] shadow-xl" style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
      <p className="text-right text-[12px] text-[#555]">Sample facilitation script · Psy180 demo</p>
      {script === "grounding" ? <GroundingScript /> : <ProcessingScript />}
      <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-wide text-[#999]">
        Original sample wording for demonstration only · not clinical guidance · therapists bring their own scripts
      </p>
    </div>
  );
}

/** Original grounding/resourcing sample (not a branded protocol). */
function GroundingScript() {
  return (
    <>
      <h2 className="mt-4 text-center text-[20px] font-bold">GROUNDING &amp; RESOURCING</h2>
      <p className="mt-1 text-center text-[12px] italic text-[#555]">A calm opening, before any focused work begins</p>
      <p className="mt-6 text-[14px]">&ldquo;Before we begin, let&rsquo;s take a moment to settle. There&rsquo;s nothing to fix right now — we&rsquo;re just getting comfortable.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;Find a position that feels steady. Let your shoulders drop. Notice the chair supporting you and your feet on the floor.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;When you&rsquo;re ready, let your eyes follow the movement on the screen. You don&rsquo;t have to concentrate — just let your gaze travel with it, side to side.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;Now bring to mind a place — real or imagined — where you feel calm and safe. A room, a shoreline, a memory. Picture it as clearly as you can.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;What do you notice there? The colors, the sounds, the temperature. Let yourself stay with it for a moment.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;Notice where the calm sits in your body — maybe your chest, your hands, your breath. Stay with that as you keep following the movement.&rdquo;</p>
      <p className="mt-4 text-[14px]"><strong>Anytime you need to, you can return to this place. We&rsquo;ll come back to it before we finish.</strong></p>
    </>
  );
}

/** Original generic bilateral-stimulation processing sample (not a branded protocol). */
function ProcessingScript() {
  return (
    <>
      <h2 className="mt-4 text-center text-[20px] font-bold">TARGET PROCESSING SEQUENCE</h2>
      <p className="mt-1 text-center text-[12px] italic text-[#555]">Working through the focus you chose together</p>
      <p className="mt-6 text-[14px]">&ldquo;Bring to mind the topic we agreed to work on today. You don&rsquo;t need to relive it — just let it be present, the way it is right now.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;As you hold it in mind, notice where you feel it in your body. There&rsquo;s no right answer — wherever your attention lands is fine.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;Let your eyes follow the movement, and just notice whatever comes up — thoughts, images, feelings, sensations. Let them come and go. Nothing to change.&rdquo;</p>
      <p className="mt-4 text-[14px]">✋ <strong>Pause the movement.</strong> &ldquo;Take a breath. What are you noticing now?&rdquo; <span className="text-[#555]">(Let the client respond, then resume.)</span></p>
      <p className="mt-3 text-[14px]">&ldquo;Go with that. Keep following the movement and let it keep moving on its own.&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;If a strong feeling stays put, you can give it a shape — a color, an object, a weight. See what your mind offers.&rdquo;</p>
      <p className="mt-4 text-[14px]"><strong>Check in:</strong> &ldquo;On a scale of 0 to 10, how much charge does it carry now, compared with when we started?&rdquo;</p>
      <p className="mt-3 text-[14px]">&ldquo;When you&rsquo;re ready, let your attention come back to the room — your feet, the chair, your breath. We&rsquo;ll pause here.&rdquo;</p>
    </>
  );
}

/** SOUNDS sub-view — the Therapeutic Soundboard library (cards from SOUND_LIBRARY). */
function SoundsView({ onAddToSession }: { onAddToSession: () => void }) {
  const cats = ["All", "Theta", "Alpha", "Delta", "Gamma", "Solfeggio", "Noise", "Tuning"];
  const [cat, setCat] = useState("All");
  const shown = SOUND_LIBRARY.filter((s) => cat === "All" || s.category === cat);
  return (
    <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-5">
      <h2 className="font-heading text-2xl font-medium text-[var(--color-text-primary)]">Therapeutic Soundboard</h2>
      <p className="text-[13px] text-[var(--color-text-ghost)]">Clinical-grade therapeutic frequency generator — 30–60s samples (demo).</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <button key={c} type="button" onClick={() => setCat(c)}
            className={cn("rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors",
              cat === c ? "border-[var(--color-gold)] bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-gold)]")}>
            {c}
          </button>
        ))}
      </div>
      <ul className="mt-4 space-y-2.5">
        {shown.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-4 py-3">
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-heading text-[15px] font-medium text-[var(--color-text-primary)]">
                {s.name} {s.requires_headphones && <Headphones className="h-3.5 w-3.5 text-[var(--color-text-ghost)]" />}
              </p>
              <p className="text-[12px] text-[var(--color-text-ghost)]">{s.purpose}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge text={s.type} />
                {s.carrierHz && <span className="font-mono text-[10px] text-[var(--color-text-muted)]">{s.carrierHz} Hz</span>}
                <Badge text={s.studyStatus.replace(/_/g, " ")} tone="study" />
                <Badge text={s.evidenceStrength.replace(/_/g, " ")} tone="evidence" />
              </div>
            </div>
            <button type="button" onClick={onAddToSession}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--color-border-gold)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-light)]">
              <Plus className="h-3 w-3" /> Session
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Badge({ text, tone }: { text: string; tone?: "study" | "evidence" }) {
  const cls = tone === "study" ? "border-[#2e6b8a] text-[#9cc8e0]" : tone === "evidence" ? "border-[#6b8a3a] text-[#bcd98a]" : "border-[var(--color-border)] text-[var(--color-text-ghost)]";
  return <span className={cn("rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide", cls)}>{text}</span>;
}

/** Persistent perspective switch (Scope A10) — flips the session view between the
 *  therapist's controls and the client's-eye view, on the fly, no reload. */
function PerspectiveToggle({ perspective, onChange }: { perspective: "therapist" | "client"; onChange: (p: "therapist" | "client") => void }) {
  const opts = [
    { key: "therapist" as const, label: "Therapist view", icon: <Stethoscope className="h-3.5 w-3.5" /> },
    { key: "client" as const, label: "Client view", icon: <User className="h-3.5 w-3.5" /> },
  ];
  return (
    <div data-tour="perspective-toggle" className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">Live session</p>
        <p className="text-[13px] text-[var(--color-text-muted)]">
          Flip the view to see exactly what your client sees on their device.{" "}
          <HintTag id="hint-client-view" title="Client view" align="left">
            In a real session your view and the client&apos;s stay in sync, frame for frame, on any device. Here it&apos;s
            bridged in one tab so you can preview the permission steps before sending a client a link.
          </HintTag>
        </p>
      </div>
      <div role="tablist" aria-label="Session perspective" className="inline-flex rounded-lg border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-1">
        {opts.map((o) => {
          const active = perspective === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(o.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 font-mono text-[11px] uppercase tracking-wide transition-colors",
                active ? "bg-[var(--color-gold)] text-[var(--color-bg-deep)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-gold-soft)]",
              )}
            >
              {o.icon} {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
